import React, { useState } from "react";
import { reelDurationFrames, FPS } from "@hudhud/reel";
import { LANG_NAMES, type CaptionLang } from "@hudhud/core";
import { Icon } from "../components/Icon.js";
import { Menu } from "../components/Menu.js";
import { useEditor } from "./store.js";
import { usePhone } from "../hooks/useMedia.js";

const fmt = (ms: number) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, "0")}.${Math.floor((ms % 1000) / 100)}`;
const SPEEDS = [0.5, 1, 1.5, 2];

/** One toggle in the options bar: an icon, a short word, pressed = gold. */
const TBtn: React.FC<{ icon: string; label: string; on?: boolean; title: string; onClick: () => void; wide?: boolean }> = ({ icon, label, on, title, onClick, wide }) => (
  <button className={`tbtn${on ? " on" : ""}`} onClick={onClick} title={title} aria-pressed={on} aria-label={label} style={wide ? { minWidth: 52 } : undefined}><Icon name={icon} size={15} /><span>{label}</span></button>
);

/**
 * The transport under the preview: a scrubber you can drag, the timecode, the play cluster in the middle, and the viewing options
 * grouped on the right (loop, safe zone, speed, language). On a phone: play, time, and one menu.
 */
export const Transport: React.FC<{ width: number }> = ({ width }) => {
  const clip = useEditor((s) => s.clip);
  const playheadMs = useEditor((s) => s.playheadMs);
  const playing = useEditor((s) => s.playing);
  const loop = useEditor((s) => s.loop);
  const speed = useEditor((s) => s.speed);
  const safeZone = useEditor((s) => s.safeZone);
  const previewLang = useEditor((s) => s.previewLang);
  const exact = useEditor((s) => s.exactPreview);
  const setExactPreview = useEditor((s) => s.setExactPreview);
  const phone = usePhone();
  const [hoverPct, setHoverPct] = useState<number | null>(null);
  const { setPlayhead, setPlaying, setLoop, setSpeed, setSafeZone, setPreviewLang } = useEditor.getState();
  if (!clip) return null;
  const langs: CaptionLang[] = ["en", ...clip.versionLangs.filter((l) => clip.versions[l])];
  const totalMs = (reelDurationFrames(clip) / FPS) * 1000;
  const pct = Math.min(100, (playheadMs / totalMs) * 100);
  const step = (ms: number) => { setPlaying(false); setPlayhead(Math.max(0, Math.min(totalMs - 1, playheadMs + ms))); };
  /** drag the scrubber: the picture follows the finger; if the clip was playing it plays on from where the finger lets go */
  const scrub = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const wasPlaying = useEditor.getState().playing;
    if (wasPlaying) setPlaying(false);
    const move = (ev: PointerEvent) => { const x = Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width)); useEditor.getState().scrubTo(x * totalMs); };
    move(e.nativeEvent);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); if (wasPlaying) setTimeout(() => setPlaying(true), 60); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const nextSpeed = () => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length] ?? 1);
  const play = <button className={`tplay${playing ? " playing" : ""}`} onClick={() => setPlaying(!playing)} title="Play / pause (space)" aria-label={playing ? "Pause" : "Play"}><Icon name={playing ? "pause" : "play"} size={phone ? 24 : 22} color="var(--on-gold)" sw={0} /></button>;
  const scrubber = (
    <div className="tscrub" onPointerDown={scrub} onPointerMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHoverPct(Math.max(0, Math.min(1, (e.clientX - r.left) / r.width))); }} onPointerLeave={() => setHoverPct(null)} role="slider" aria-label="Position" aria-valuemin={0} aria-valuemax={Math.round(totalMs)} aria-valuenow={Math.round(playheadMs)} aria-valuetext={fmt(playheadMs)} tabIndex={0} onKeyDown={(e) => { if (e.key === "ArrowLeft") step(-1000); if (e.key === "ArrowRight") step(1000); }}>
      <div className="track"><i style={{ width: `${pct}%` }} /></div>
      {hoverPct != null && !phone ? <span className="tip" style={{ left: `${hoverPct * 100}%` }}>{fmt(hoverPct * totalMs)}</span> : null}
      <div className="knob" style={{ left: `${pct}%` }} />
    </div>
  );
  const time = <span className="ttime num" aria-live="off"><b>{fmt(playheadMs)}</b><span className="muted"> / {fmt(totalMs)}</span></span>;

  if (phone) {
    return (
      <div className="transport phone" style={{ width }}>
        {scrubber}
        <div className="row" style={{ gap: 10 }}>
          {play}
          {time}
          <span className="spacer" />
          {langs.length > 1 ? <select value={previewLang} onChange={(e) => setPreviewLang(e.target.value as CaptionLang)} aria-label="Preview language" style={{ padding: "8px 26px 8px 10px", fontSize: 13 }}>{langs.map((l) => <option key={l} value={l}>{l === "en" ? "English" : LANG_NAMES[l] ?? l}</option>)}</select> : null}
          <Menu title="Viewing options" className="btn sm quiet" align="right">
            <button onClick={() => step(-1000 / FPS)}><Icon name="step-back" size={14} />Back one frame</button>
            <button onClick={() => step(1000 / FPS)}><Icon name="step-fwd" size={14} />Forward one frame</button>
            <hr />
            <button onClick={nextSpeed}><Icon name="gauge" size={14} />Speed<small>{speed}×</small></button>
            <button onClick={() => setLoop(!loop)}><Icon name="loop" size={14} />Loop<small>{loop ? "on" : "off"}</small></button>
            <button onClick={() => setSafeZone(!safeZone)}><Icon name="safe" size={14} />Safe zone<small>{safeZone ? "on" : "off"}</small></button>
            <button onClick={() => setExactPreview(!exact)}><Icon name="film" size={14} />Render effects<small>{exact ? "on · slower" : "off"}</small></button>
          </Menu>
        </div>
      </div>
    );
  }
  return (
    <div className="transport" style={{ width }}>
      {scrubber}
      <div className="trow">
        {time}
        <div className="tcluster">
          <button className="tbtn" onClick={() => { setPlaying(false); setPlayhead(0); }} title="Go to the start (Home)" aria-label="Go to the start"><Icon name="skip-start" size={15} /></button>
          <button className="tbtn" onClick={() => step(-1000 / FPS)} title="Back one frame (←) · Shift+← one second" aria-label="Back one frame"><Icon name="step-back" size={15} /></button>
          {play}
          <button className="tbtn" onClick={() => step(1000 / FPS)} title="Forward one frame (→) · Shift+→ one second" aria-label="Forward one frame"><Icon name="step-fwd" size={15} /></button>
          <button className="tbtn" onClick={() => { setPlaying(false); setPlayhead(Math.max(0, totalMs - 1)); }} title="Go to the end (End)" aria-label="Go to the end"><Icon name="skip-end" size={15} /></button>
        </div>
        <div className="tbar" role="toolbar" aria-label="Viewing options">
          <TBtn icon="loop" label="Loop" on={loop} title="Play the clip round and round" onClick={() => setLoop(!loop)} />
          <TBtn icon="safe" label="Safe" on={safeZone} title="Show the area every platform keeps clear of its own buttons" onClick={() => setSafeZone(!safeZone)} />
          <TBtn icon="gauge" label={`${speed}×`} on={speed !== 1} title="Playback speed: click to cycle ½×, 1×, 1½×, 2×" onClick={nextSpeed} />
          {langs.length > 1 ? <div className="tlang" title="Preview a language version"><Icon name="globe" size={14} /><select value={previewLang} onChange={(e) => setPreviewLang(e.target.value as CaptionLang)} aria-label="Preview language">{langs.map((l) => <option key={l} value={l}>{l === "en" ? "English" : LANG_NAMES[l] ?? l}</option>)}</select></div> : null}
        </div>
      </div>
    </div>
  );
};
